import React from 'react';
import { Button } from 'react-bootstrap';
import { connect } from 'react-redux';
import { Cell } from 'react-sticky-table';
import { ccsdCodeCostSingleFetch, ccsdCodeCostSingleCreate, ccsdCodeCostSingleUpdate } from '../../lib/redux/actions/ccsdCodeCosts';
import { datedCostSingleCreate } from '../../lib/redux/actions/datedCosts';
import moment from 'moment';
import { ccsdCodeSingleFetch } from '../../lib/redux/actions/ccsdCodes';
import { isSeniorStaff } from '../../lib/helper/authorisation';
import { generalCurrencyFormatter } from '../../lib/helper/formatting';
import { addToast } from '../../lib/redux/actions/toast';
import CcsdCodeCostAndTimelineIcon from './CcsdCodeCostAndTimelineIcon';

const mapDispatchToProps = {
  datedCostSingleCreate,
  ccsdCodeCostSingleFetch,
  ccsdCodeCostSingleCreate,
  ccsdCodeCostSingleUpdate,
  ccsdCodeSingleFetch,
  addToast,
};

class EditableCcsdCodeCost extends React.Component {
  constructor(props) {
    super(props);

    this.todayDate = new Date();
    this.state = {
      isEditing: false,
      fieldValue: '',
      fieldValidFrom: this.todayDate.toISOString().substring(0, 10),
    };
  }

  handleCostChange(event) {
    this.setState(() => ({ fieldValue: event.target.value }));
  }

  handleDateChange(event) {
    this.setState(() => ({ fieldValidFrom: event.target.value }));
  }

  startEditing() {
    if (!isSeniorStaff()) {
      return;
    }
    this.setState(() => ({ isEditing: true }));
  }

  stopEditing(e) {
    e.stopPropagation();
    this.setState(() => ({ isEditing: false }));
  }

  saveAndStopEditing(e) {
    if (!isSeniorStaff()) {
      return;
    }

    if (this.state.fieldValue === '' || this.state.fieldValidFrom === '') {
      this.props.addToast('Please enter both a cost and a date.', false, true);
      return;
    }

    const { ccsdCode, consultantType, consultantHealthInsurer, ccsdCodeCost = false, defaultCcsdCodeCost = false, isFeeAssuredProcedure = false, addToast } = this.props;

    const createDatedCost = (ccsdCodeCostIri) => {
      const data = {
        cost: Number(this.state.fieldValue),
        validFrom: moment.utc(this.state.fieldValidFrom).toISOString(),
        consultantType: consultantType['@id'],
      };
      if (defaultCcsdCodeCost !== false && !isFeeAssuredProcedure) {
        data['defaultCcsdCodeCost'] = defaultCcsdCodeCost['@id'];
      } else {
        data['ccsdCodeCost'] = ccsdCodeCostIri || ccsdCodeCost['@id'];
      }
      return this.props.datedCostSingleCreate(data);
    };

    const onSuccess = () => {
      addToast('Cost added successfully', true);
      if (this.props.onSaved) {
        this.props.onSaved(defaultCcsdCodeCost ? defaultCcsdCodeCost['@id'] : null);
      } else if (!ccsdCodeCost || !ccsdCodeCost.id) {
        this.props.ccsdCodeSingleFetch(ccsdCode.id);
      } else {
        this.props.ccsdCodeCostSingleFetch(ccsdCodeCost.id);
      }
    };

    if (isFeeAssuredProcedure && (!ccsdCodeCost || !ccsdCodeCost.id)) {
      // No CcsdCodeCost exists yet — create one with isOverride, then create DatedCost
      this.props.ccsdCodeCostSingleCreate({
        ccsdCode: `/api/ccsd_codes/${ccsdCode.id}`,
        consultantHealthInsurer: consultantHealthInsurer['@id'],
        isOverride: true,
      }).then((response) => {
        createDatedCost(response['@id']).then(onSuccess);
      });
    } else if (isFeeAssuredProcedure && ccsdCodeCost && !ccsdCodeCost.isOverride) {
      // CcsdCodeCost exists but not yet an override — set isOverride, then create DatedCost
      this.props.ccsdCodeCostSingleUpdate({ isOverride: true }, ccsdCodeCost.id).then(() => {
        createDatedCost().then(onSuccess);
      });
    } else {
      // Normal flow (non-fee-assured, or already an override)
      createDatedCost().then(onSuccess);
    }
    this.stopEditing(e);
  }

  resetToDefault(e) {
    e.stopPropagation();
    const { ccsdCodeCost, addToast } = this.props;
    if (!ccsdCodeCost || !ccsdCodeCost.id) return;

    this.props.ccsdCodeCostSingleUpdate({ isOverride: false }, ccsdCodeCost.id).then(() => {
      addToast('Reset to default cost', true);
      this.props.ccsdCodeCostSingleFetch(ccsdCodeCost.id);
    });
  }

  renderContent() {
    const { consultantType, ccsdCodeCost = false, defaultCcsdCodeCost = false, isFeeAssuredProcedure = false } = this.props;

    const ccsdCodeCostToUse = ccsdCodeCost ? ccsdCodeCost : defaultCcsdCodeCost;
    const isOverride = isFeeAssuredProcedure && ccsdCodeCost && ccsdCodeCost.isOverride;
    const defaultCostForType = isOverride && ccsdCodeCost.defaultCurrentCosts
      ? (ccsdCodeCost.defaultCurrentCosts.find((dc) => String(dc.consultantType?.code) === String(consultantType.code))?.cost ?? null)
      : null;

    return this.state.isEditing ? (
      <div>
        <input
          autoFocus
          type="number"
          placeholder="0.00"
          step="0.01"
          min="0.00"
          title="New Cost"
          value={this.state.fieldValue}
          onChange={this.handleCostChange.bind(this)}
          className="form-control mb-1"
        />
        <input
          type="date"
          title="Cost Valid From Date"
          value={this.state.fieldValidFrom}
          onChange={this.handleDateChange.bind(this)}
          className="form-control mb-1"
        />
        <Button variant="primary" title="Save Cost Change" size="sm" className="mb-0" onClick={this.saveAndStopEditing.bind(this)}>
          <i className="fas fa-check" />
        </Button>
        <Button variant="danger" title="Cancel Cost Change" size="sm" className="mb-0" onClick={this.stopEditing.bind(this)}>
          <i className="fas fa-times" />
        </Button>
      </div>
    ) : (
      <>
        <CcsdCodeCostAndTimelineIcon ccsdCodeCostToUse={ccsdCodeCostToUse} consultantType={consultantType} isDefaultCost={!!defaultCcsdCodeCost || (isFeeAssuredProcedure && !isOverride)} isOverride={isOverride} />
        {isOverride && isSeniorStaff() && (
          <Button
            variant="link"
            size="sm"
            className="p-0 ml-1 text-muted"
            title={`Fee assured cost overridden, click here to reset to fee-assured price${defaultCostForType != null ? ` (${generalCurrencyFormatter(defaultCostForType)})` : ''}`}
            onClick={this.resetToDefault.bind(this)}
          >
            <i className="fas fa-undo-alt" />
          </Button>
        )}
      </>
    );
  }

  render() {
    const {
      ccsdCode = { reference: '' },
      healthInsurer,
      consultantHealthInsurer = false,
      isCell = false,
      ccsdCodeCost = false,
      defaultCcsdCodeCost = false,
    } = this.props;

    let labelToUse = healthInsurer.name;
    if (consultantHealthInsurer && consultantHealthInsurer.healthInsurer.canHaveOverrideLabel && consultantHealthInsurer.overrideLabel) {
      labelToUse = consultantHealthInsurer.overrideLabel;
    }
    const ok = ((healthInsurer?.id && defaultCcsdCodeCost?.id) || (consultantHealthInsurer?.id && ccsdCodeCost?.id)) && ccsdCode?.id;

    return isCell ? (
      <Cell className="text-right border-right" title={`Edit ${ccsdCode.reference} for ${labelToUse}`} onClick={this.startEditing.bind(this)}>
        {this.renderContent()}
        {!ok && (
          <p>
            <i
              title="There is a problem with the data for this cell"
              data-info={`hi:${healthInsurer.id}|chi:${consultantHealthInsurer.id}|cc:${ccsdCode.id}|ccc:${ccsdCodeCost.id}|dccc:${defaultCcsdCodeCost.id}`}
              className="text-danger fa fa-exclamation-triangle"
            />
          </p>
        )}
      </Cell>
    ) : (
      <td className="text-right" title={`Edit ${ccsdCode.reference} for ${labelToUse}`} onClick={this.startEditing.bind(this)}>
        {this.renderContent()}
        {!ok && (
          <p>
            <i
              title="There is a problem with the data for this cell"
              data-info={`hi:${healthInsurer.id}|chi:${consultantHealthInsurer.id}|cc:${ccsdCode.id}|ccc:${ccsdCodeCost.id}|dccc:${defaultCcsdCodeCost.id}`}
              className="text-danger fa fa-exclamation-triangle"
            />
          </p>
        )}
      </td>
    );
  }
}

export default connect(null, mapDispatchToProps)(EditableCcsdCodeCost);
